import { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, Users, CheckCircle, Award, Shield, Crown, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';

const API = `${window.location.origin}/api`;

const BADGE_CONFIG = {
  platinum: {
    icon: Crown,
    label: 'Platinum',
    color: 'from-purple-400 via-pink-300 to-purple-400',
    textColor: 'text-purple-300',
    bgColor: 'bg-purple-500/20',
    borderColor: 'border-purple-500/30',
    description: '50+ votes, 4.8+ rating, 10+ reviews'
  },
  gold: {
    icon: Award,
    label: 'Gold',
    color: 'from-yellow-400 via-amber-300 to-yellow-400',
    textColor: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-500/30',
    description: '25+ votes, 4.5+ rating'
  },
  silver: {
    icon: Shield,
    label: 'Silver',
    color: 'from-gray-300 via-white to-gray-300',
    textColor: 'text-gray-300',
    bgColor: 'bg-gray-500/20',
    borderColor: 'border-gray-500/30',
    description: '10+ community confirmations'
  },
  bronze: {
    icon: CheckCircle,
    label: 'Bronze',
    color: 'from-orange-400 via-amber-500 to-orange-400',
    textColor: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
    borderColor: 'border-orange-500/30',
    description: 'Admin verified'
  }
};

export function BadgeDisplay({ level, size = 'md' }) {
  if (!level || !BADGE_CONFIG[level]) return null;
  
  const config = BADGE_CONFIG[level];
  const Icon = config.icon;
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5'
  };
  
  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };
  
  return (
    <span 
      className={`inline-flex items-center gap-1.5 rounded-full ${config.bgColor} ${config.borderColor} border ${config.textColor} font-medium ${sizeClasses[size]}`}
      title={config.description}
    >
      <Icon className={iconSizes[size]} />
      {config.label}
    </span>
  );
}

export default function CommunityVoting({ businessId }) {
  const [verification, setVerification] = useState(null);
  const [myVote, setMyVote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [businessId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [verRes, voteRes] = await Promise.all([
        fetch(`${API}/businesses/${businessId}/verification`),
        fetch(`${API}/businesses/${businessId}/my-vote`, { credentials: 'include' })
      ]);

      if (verRes.ok) {
        const data = await verRes.json();
        setVerification(data);
      }

      if (voteRes.ok) {
        const data = await voteRes.json();
        setMyVote(data);
      }
    } catch (error) {
      console.error('Error fetching verification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (isBlackOwned) => {
    setVoting(true);
    try {
      const response = await fetch(`${API}/businesses/${businessId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_black_owned: isBlackOwned })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchData();
      } else if (response.status === 401) {
        toast.error('Please sign in to vote');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit vote');
      }
    } catch (error) {
      toast.error('Error submitting vote');
    } finally {
      setVoting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 rounded-xl bg-zinc-900/50 border border-zinc-800 animate-pulse">
        <div className="h-6 w-48 bg-zinc-800 rounded mb-4" />
        <div className="h-4 w-full bg-zinc-800 rounded" />
      </div>
    );
  }

  if (!verification) return null;

  const progressPercent = Math.min((verification.total_votes / 10) * 100, 100);
  const confirmPercent = verification.total_votes > 0 
    ? (verification.confirmed_votes / verification.total_votes) * 100 
    : 0;

  return (
    <div className="p-6 rounded-xl bg-zinc-900/50 border border-zinc-800">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-zinc-400" />
          <h3 className="font-semibold text-white">Community Verification</h3>
        </div>
        {verification.badge_level && (
          <BadgeDisplay level={verification.badge_level} />
        )}
      </div>

      {/* Status */}
      {verification.verification_status === 'verified' ? (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30 mb-4">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-green-400 font-medium">Verified Black-Owned Business</span>
        </div>
      ) : verification.verification_status === 'pending_community' ? (
        <div className="mb-4">
          <p className="text-zinc-400 text-sm mb-2">
            Help verify this business! {verification.needs_votes > 0 
              ? `${verification.needs_votes} more votes needed.` 
              : 'Awaiting more confirmations.'}
          </p>
          <Progress value={progressPercent} className="h-2" />
          <p className="text-zinc-500 text-xs mt-1">
            {verification.total_votes}/10 votes ({verification.confirmed_votes} confirmations)
          </p>
        </div>
      ) : null}

      {/* Vote Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="p-3 rounded-lg bg-zinc-800/50">
          <div className="flex items-center gap-2 text-green-400 mb-1">
            <ThumbsUp className="w-4 h-4" />
            <span className="font-semibold">{verification.confirmed_votes}</span>
          </div>
          <p className="text-zinc-500 text-xs">Confirmed</p>
        </div>
        <div className="p-3 rounded-lg bg-zinc-800/50">
          <div className="flex items-center gap-2 text-red-400 mb-1">
            <ThumbsDown className="w-4 h-4" />
            <span className="font-semibold">{verification.disputed_votes}</span>
          </div>
          <p className="text-zinc-500 text-xs">Disputed</p>
        </div>
      </div>

      {/* Confirmation Rate */}
      {verification.total_votes > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-zinc-500 mb-1">
            <span>Confirmation Rate</span>
            <span>{confirmPercent.toFixed(0)}%</span>
          </div>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${confirmPercent}%` }}
              className={`h-full ${confirmPercent >= 70 ? 'bg-green-500' : confirmPercent >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
            />
          </div>
        </div>
      )}

      {/* Voting Buttons */}
      {myVote?.has_voted ? (
        <div className="p-3 rounded-lg bg-zinc-800/50 text-center">
          <p className="text-zinc-400 text-sm">
            You voted: {myVote.vote ? (
              <span className="text-green-400">Yes, Black-owned</span>
            ) : (
              <span className="text-red-400">Not sure</span>
            )}
          </p>
        </div>
      ) : (
        <div>
          <p className="text-zinc-400 text-sm mb-3 text-center">
            Is this a Black-owned business?
          </p>
          <div className="flex gap-3">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => handleVote(true)}
              disabled={voting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-green-500/20 border border-green-500/30 text-green-400 font-medium hover:bg-green-500/30 transition-colors disabled:opacity-50"
              data-testid="vote-yes"
            >
              <ThumbsUp className="w-4 h-4" />
              Yes
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => handleVote(false)}
              disabled={voting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-400 font-medium hover:bg-zinc-700 transition-colors disabled:opacity-50"
              data-testid="vote-no"
            >
              <ThumbsDown className="w-4 h-4" />
              Not Sure
            </motion.button>
          </div>
        </div>
      )}

      {/* Badge Progress */}
      {verification.badge_level && verification.badge_level !== 'platinum' && (
        <div className="mt-4 pt-4 border-t border-zinc-800">
          <p className="text-zinc-500 text-xs">
            {verification.badge_level === 'bronze' && 'Reach Silver: Get 10+ community confirmations'}
            {verification.badge_level === 'silver' && 'Reach Gold: Get 25+ votes and 4.5+ rating'}
            {verification.badge_level === 'gold' && 'Reach Platinum: Get 50+ votes, 4.8+ rating, 10+ reviews'}
          </p>
        </div>
      )}
    </div>
  );
}
